/**
 * EditFormDialog - reusable edit modal driven by FormFieldMeta.
 *
 * Generates a complete edit form from field metadata using the generated
 * `useForm` system. Used by EditableDetailCard and EditableDetailGridCard,
 * but also available standalone.
 *
 * Features:
 * - Auto-generates form fields from FormFieldMeta[]
 * - Zod validation from field metadata
 * - Optional edit reason (audit trail) via built-in textarea
 * - Consistent Clara Vale dialog styling
 *
 * Usage:
 *   <EditFormDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Edit Customer"
 *     fields={fields}
 *     values={currentValues}
 *     onSave={async (values) => { await mutation.mutateAsync(values) }}
 *     isPending={mutation.isPending}
 *   />
 */

import { useState, useEffect, useMemo, useRef, type ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { useForm } from "@/shared/lib/forms";
import { capitalizeFirstLetter } from "@/shared/lib/utils";
import { buildFormFieldFromMeta } from "@/shared/lib/forms/generated-metadata";
import { VehicleLookupField } from "@/admin/components/shared/vehicle-lookup-field";
import { isVehicleLookupField } from "@/admin/components/shared/vehicle-lookup";
import type { FormContext } from "@/shared/lib/forms/generated-types";
import type { FormFieldMeta, SubmissionMode } from "@/shared/lib/forms/types";
import type { EditSaveHelpers } from "@/admin/components/shared/editable-card-types";
import {
  normaliseObjectValuedSubmission,
  seedObjectValuedValues,
} from "./object-valued-fields";

export interface EditFormDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Callback to toggle open state. */
  onOpenChange: (open: boolean) => void;
  /** Dialog title shown in the header. */
  title: string;
  /** Field metadata defining the form. */
  fields: FormFieldMeta[];
  /** Current values to populate the form. */
  values: Record<string, unknown>;
  /**
   * Called with updated values when the user saves. Resolve `false` to keep
   * the dialog open (nothing saved, no error).
   */
  onSave: (
    values: Record<string, unknown>,
    reason?: string,
    helpers?: EditSaveHelpers,
  ) => Promise<void | boolean>;
  /** Whether the save operation is in progress. */
  isPending?: boolean;
  /** Whether to show an edit reason textarea (audit trail). */
  requireReason?: boolean;
  /** Submission mode for validation. Defaults to 'submit'. */
  submissionMode?: SubmissionMode;
  /** File upload handler passed through to MetadataField. */
  onFileUpload?: (
    file: File,
    field: FormFieldMeta,
  ) => Promise<{
    uuid: string;
    file_name: string;
    file_url: string;
  }>;
  /**
   * Notices computed from a field's live value, keyed by ``code_name`` and
   * rendered under the fields - e.g. a warning that the address being typed is
   * already another customer's. Advisory only: a notice never blocks saving.
   */
  fieldNotices?: Record<string, (value: unknown) => ReactNode>;
}

export function EditFormDialog({
  open,
  onOpenChange,
  title,
  fields,
  values,
  onSave,
  isPending = false,
  requireReason = false,
  submissionMode = "submit",
  onFileUpload,
  fieldNotices,
}: EditFormDialogProps) {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setReason("");
      setReasonError(false);
    }
    onOpenChange(nextOpen);
  }
  function handleClose() {
    setReason("");
    setReasonError(false);
    onOpenChange(false);
  }
  // Cascading SELECT options (configuration_data.depends_on /
  // option_groups) are resolved live inside SelectField against the form's
  // An empty picker / address opens on `null`, never `''` - see
  // `seedObjectValuedValues`. Applied here so every caller of this dialog gets
  // it, not just the one card that first hit the bug.
  const seededValues = useMemo(
    () => seedObjectValuedValues(fields, values),
    [fields, values],
  );

  // Live values of the fields that carry a notice, seeded on open.
  const [noticeValues, setNoticeValues] = useState<Record<string, unknown>>({});
  useEffect(() => {
    if (!open || !fieldNotices) return;
    const seeded: Record<string, unknown> = {};
    for (const code of Object.keys(fieldNotices)) seeded[code] = seededValues[code];
    setNoticeValues(seeded);
  }, [open, fieldNotices, seededValues]);

  // current values, so we just pass field metadata straight through here.
  const generatedFields = useMemo(
    () =>
      fields.map((field) => {
        // A field that declares a lookup provider gets the same input here as
        // it gets on the creation form. Without this, correcting a plate is a
        // plain text box on the one surface where getting it wrong is most
        // likely - the operator is retyping a value someone already mistyped -
        // while creating the same record offers the lookup. Address fields
        // already behave this way through their field type; the vehicle
        // provider has no type of its own, so it is read from the metadata.
        if (isVehicleLookupField(field)) {
          return {
            type: "custom" as const,
            name: field.code_name,
            label: field.display_name,
            value: seededValues[field.code_name] ?? "",
            render: (formCtx: FormContext) => (
              <VehicleLookupField
                field={field}
                fields={fields}
                formCtx={formCtx}
              />
            ),
          };
        }
        const built = buildFormFieldFromMeta(
          field,
          seededValues[field.code_name],
          submissionMode,
        );
        if (!fieldNotices?.[field.code_name]) return built;
        return {
          ...built,
          onChange: (value: unknown, formCtx?: FormContext) => {
            built.onChange?.(value, formCtx);
            setNoticeValues((prev) => ({ ...prev, [field.code_name]: value }));
          },
        };
      }),
    [fields, submissionMode, seededValues, fieldNotices],
  );
  const formConfig = useMemo(
    () => ({
      name: "edit-form-dialog",
      submit: "footer" as const,
      fields: generatedFields,
      uploadFile: onFileUpload
        ? async ({ file, use_case }: { file: File; use_case: string }) => {
            const targetField = fields.find(
              (field) => field.file_use_case === use_case,
            );
            if (!targetField) {
              throw new Error(
                `No upload field found for use case: ${use_case}`,
              );
            }
            return onFileUpload(file, targetField);
          }
        : undefined,
    }),
    [fields, generatedFields, onFileUpload],
  );
  // Validate reason in handleSave, not here. Early returns in this callback
  // can leave useForm's loading overlay stuck on.
  const { Form, ref } = useForm(
    formConfig,
    async (formValues, formContext) => {
      const trimmedReason = reason.trim();
      // Hand the form's setFieldError to onSave so a server-side conflict (e.g.
      // a uniqueness guard) can render inline on the offending field. When
      // onSave throws after setting field errors, handleClose() below is skipped,
      // so the dialog stays open on the error.
      const saved = await onSave(
        normaliseObjectValuedSubmission(fields, seededValues, formValues),
        requireReason ? trimmedReason : undefined,
        { setFieldError: formContext.setFieldError },
      );
      if (saved === false) return;
      handleClose();
    },
    [open, seededValues, fields, submissionMode, requireReason],
  );

  function handleSave() {
    if (requireReason && !reason.trim()) {
      setReasonError(true);
      return;
    }
    ref.current?.submitForm();
  }

  // Reset to the stored values only as the dialog opens. While it is open a
  // refetch re-seeds through useForm, which keeps the user's edits.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      ref.current?.resetForm({ values: seededValues });
    }
    wasOpenRef.current = open;
  }, [open, ref, seededValues]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-full max-w-[520px]"
        showCloseButton={false}
        bare
      >
        <DialogHeader className="border-b-[0.5px] border-b-[var(--rule)] px-5 py-4">
          <DialogTitle className="text-[18px] font-medium text-[var(--ink)]">
            {title}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="px-5 py-4">
          <Form className="flex flex-col gap-4" />
          {fieldNotices
            ? Object.entries(fieldNotices).map(([code, render]) => (
                <div key={code} className="mt-3">
                  {render(noticeValues[code])}
                </div>
              ))
            : null}
          {requireReason && (
            <div className="mt-4 border-t-[0.5px] border-t-[var(--rule-light)] pt-4">
              <p className="mb-1.5 text-[14px] text-[var(--ink-2)]">
                Provide reason
                <span
                  aria-hidden="true"
                  className="ml-[2px] inline-block align-middle text-[17px] leading-[4px] text-[var(--status-error)]"
                >
                  *
                </span>
              </p>
              <Textarea
                data-testid="edit-form-reason-input"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (reasonError) setReasonError(false);
                }}
                onBlur={() => setReason(capitalizeFirstLetter(reason))}
                placeholder="e.g. Customer called to update address..."
                rows={2}
                className={`min-h-0 resize-none text-sm ${reasonError ? "border-[var(--status-error)]" : ""}`}
              />
              {reasonError && (
                <p className="mt-1 text-[11px] text-[var(--status-error)]">
                  Please provide a reason for this edit
                </p>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter className="flex items-center justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] px-5 py-3">
          <Button
            type="button"
            onClick={handleClose}
            variant="outline"
            data-testid="form-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isPending}
            data-testid="form-submit-button"
          >
            {isPending ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
